import React, { useRef, useState, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  X,
  Phone,
  MessageCircle,
  Sparkles,
  Shield,
  Heart,
  TrendingUp,
  Star,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const PHONE_NUMBER = "+971541873527";
const WHATSAPP_BASE = "https://wa.me/971541873527?text=";

const gemstones = [
  {
    id: "blue-sapphire",
    name: "Blue Sapphire",
    hindiName: "Neelam",
    image: "../../../images/blue-sapphire.png",
    color: "from-blue-600 to-blue-900",
    glowColor: "rgba(37, 99, 235, 0.5)",
    zodiacSigns: ["Capricorn", "Aquarius"],
    planet: "Saturn",
    benefits: [
      "Brings rapid wealth and prosperity",
      "Enhances mental clarity and focus",
      "Protects from enemies and negative energies",
      "Accelerates career growth and success",
    ],
    whoShouldWear:
      "Those facing delays in career, financial struggles, or Saturn-related doshas. Highly recommended for business professionals and entrepreneurs.",
    outcomes: [
      { icon: TrendingUp, text: "Wealth & Success" },
      { icon: Shield, text: "Protection" },
      { icon: Star, text: "Fame & Recognition" },
    ],
    caution:
      "Must be worn only after proper astrological consultation as it can have adverse effects if Saturn is malefic in your chart.",
  },
  {
    id: "emerald",
    name: "Emerald",
    hindiName: "Panna",
    image: "../../../images/emerald.png",
    color: "from-emerald-500 to-emerald-800",
    glowColor: "rgba(16, 185, 129, 0.5)",
    zodiacSigns: ["Gemini", "Virgo"],
    planet: "Mercury",
    benefits: [
      "Enhances intelligence and memory",
      "Improves communication skills",
      "Brings harmony in relationships",
      "Beneficial for students and scholars",
    ],
    whoShouldWear:
      "Students, writers, business communicators, and those in creative fields. Ideal for people seeking intellectual growth and better expression.",
    outcomes: [
      { icon: Sparkles, text: "Intelligence" },
      { icon: Heart, text: "Harmony" },
      { icon: TrendingUp, text: "Business Growth" },
    ],
    caution:
      "Should be worn in gold on the little finger of the right hand for maximum benefits.",
  },
  {
    id: "ruby",
    name: "Ruby",
    hindiName: "Manik",
    image: "../../../images/ruby.png",
    color: "from-red-500 to-red-800",
    glowColor: "rgba(239, 68, 68, 0.5)",
    zodiacSigns: ["Leo"],
    planet: "Sun",
    benefits: [
      "Boosts confidence and leadership qualities",
      "Brings fame, power, and authority",
      "Strengthens relationship with father",
      "Enhances vitality and life force",
    ],
    whoShouldWear:
      "Leaders, politicians, government officers, and those seeking recognition. Excellent for people in authoritative positions.",
    outcomes: [
      { icon: Star, text: "Fame & Authority" },
      { icon: Shield, text: "Vitality" },
      { icon: TrendingUp, text: "Success" },
    ],
    caution:
      "Only recommended when Sun is benefic in your horoscope. Consultation is essential before wearing.",
  },
  {
    id: "yellow-sapphire",
    name: "Yellow Sapphire",
    hindiName: "Pukhraj",
    image: "../../../images/yellow-sapphire.png",
    color: "from-yellow-400 to-amber-600",
    glowColor: "rgba(251, 191, 36, 0.5)",
    zodiacSigns: ["Sagittarius", "Pisces"],
    planet: "Jupiter",
    benefits: [
      "Attracts good fortune and prosperity",
      "Enhances wisdom and spirituality",
      "Beneficial for marriage and relationships",
      "Brings academic and professional success",
    ],
    whoShouldWear:
      "Teachers, scholars, spiritual seekers, and those facing marriage delays. Highly auspicious for women seeking marital bliss.",
    outcomes: [
      { icon: Heart, text: "Love & Marriage" },
      { icon: Sparkles, text: "Wisdom" },
      { icon: TrendingUp, text: "Prosperity" },
    ],
    caution:
      "One of the most beneficial gemstones. Best worn on Thursday morning in gold on the index finger.",
  },
  {
    id: "coral",
    name: "Red Coral",
    hindiName: "Moonga",
    image: "../../../images/red-coral.png",
    color: "from-orange-500 to-red-600",
    glowColor: "rgba(249, 115, 22, 0.5)",
    zodiacSigns: ["Aries", "Scorpio"],
    planet: "Mars",
    benefits: [
      "Overcomes fear and boosts courage",
      "Helps in overcoming debts and enemies",
      "Improves blood circulation and energy",
      "Beneficial for property and land matters",
    ],
    whoShouldWear:
      "Athletes, military personnel, police officers, and those in competitive fields. Excellent for people facing Manglik dosha.",
    outcomes: [
      { icon: Shield, text: "Courage" },
      { icon: TrendingUp, text: "Victory" },
      { icon: Heart, text: "Manglik Remedy" },
    ],
    caution:
      "Should be worn only if Mars is weak or afflicted in your birth chart. Wear on Tuesday in gold or copper.",
  },
  {
    id: "pearl",
    name: "Pearl",
    hindiName: "Moti",
    image: "../../../images/pearln.png",
    color: "from-slate-200 to-slate-400",
    glowColor: "rgba(203, 213, 225, 0.5)",
    zodiacSigns: ["Cancer"],
    planet: "Moon",
    benefits: [
      "Calms the mind and reduces stress",
      "Enhances emotional stability and peace",
      "Improves relationships and marital harmony",
      "Boosts memory and mental strength",
    ],
    whoShouldWear:
      "Those with a weak Moon in their horoscope, people prone to anxiety, mood swings, or emotional instability. Excellent for mothers and nurturers.",
    outcomes: [
      { icon: Heart, text: "Emotional Peace" },
      { icon: Sparkles, text: "Mental Clarity" },
      { icon: Shield, text: "Harmony" },
    ],
    caution:
      "Wear on Monday in silver on the little finger of the right hand. Ideal for those with Moon as the ruling planet.",
  },
  {
    id: "diamond",
    name: "Diamond",
    hindiName: "Heera",
    image: "../../../images/diamondn.png",
    color: "from-sky-200 to-indigo-300",
    glowColor: "rgba(186, 230, 253, 0.6)",
    zodiacSigns: ["Taurus", "Libra"],
    planet: "Venus",
    benefits: [
      "Attracts luxury, beauty, and abundance",
      "Strengthens love and romantic relationships",
      "Enhances creativity and artistic talents",
      "Brings fame in media, fashion, and arts",
    ],
    whoShouldWear:
      "Artists, designers, actors, and those in the creative or luxury industries. Also beneficial for those seeking marital bliss and lasting love.",
    outcomes: [
      { icon: Heart, text: "Love & Romance" },
      { icon: Star, text: "Fame & Luxury" },
      { icon: Sparkles, text: "Creativity" },
    ],
    caution:
      "Only suitable when Venus is well-placed in the birth chart. Wear in white gold or platinum on the middle finger on Friday.",
  },
  {
    id: "hessonite",
    name: "Hessonite",
    hindiName: "Gomed",
    image: "../../../images/hessonite.png",
    color: "from-amber-700 to-yellow-900",
    glowColor: "rgba(180, 83, 9, 0.5)",
    zodiacSigns: ["Aquarius", "Capricorn"],
    planet: "Rahu",
    benefits: [
      "Neutralizes the malefic effects of Rahu",
      "Removes confusion and brings mental clarity",
      "Boosts career in politics, law, and medicine",
      "Protects from sudden losses and misfortune",
    ],
    whoShouldWear:
      "Those going through Rahu Mahadasha or Antardasha. Suitable for lawyers, politicians, researchers, and those in unconventional careers.",
    outcomes: [
      { icon: Shield, text: "Rahu Protection" },
      { icon: TrendingUp, text: "Career Boost" },
      { icon: Sparkles, text: "Clarity" },
    ],
    caution:
      "Must be prescribed only after thorough Kundli analysis. Wear on Saturday in silver or panchdhatu on the middle finger.",
  },
  {
    id: "cats-eye",
    name: "Cat's Eye",
    hindiName: "Lahsuniya",
    image: "../../../images/cats-eye.png",
    color: "from-lime-600 to-green-900",
    glowColor: "rgba(101, 163, 13, 0.5)",
    zodiacSigns: ["Pisces", "Sagittarius"],
    planet: "Ketu",
    benefits: [
      "Provides protection from evil eye and black magic",
      "Helps recover from chronic illness and accidents",
      "Enhances spiritual growth and intuition",
      "Brings sudden gains and speculative profits",
    ],
    whoShouldWear:
      "Those suffering from Ketu Mahadasha, chronic diseases, or spiritual disturbances. Suitable for healers, mystics, and spiritual practitioners.",
    outcomes: [
      { icon: Shield, text: "Ketu Protection" },
      { icon: Sparkles, text: "Spiritual Growth" },
      { icon: TrendingUp, text: "Sudden Gains" },
    ],
    caution:
      "Extremely powerful – must only be worn after consulting an experienced astrologer. Wear in silver on the middle finger on Tuesday or Saturday.",
  },
];

// Hook to get current card width based on screen size
const useCardWidth = () => {
  const [cardWidth, setCardWidth] = useState(null);

  useEffect(() => {
    const calculate = () => {
      const vw = window.innerWidth;
      if (vw < 640) {
        // mobile: 1 card = full container width (minus px-4 * 2 = 32px)
        setCardWidth(vw - 32);
      } else if (vw < 768) {
        // sm: 2 cards
        setCardWidth((vw - 48) / 2);
      } else if (vw < 1024) {
        // md: 3 cards
        setCardWidth((vw - 64) / 3);
      } else {
        // lg+: 5 cards
        setCardWidth(null); // use CSS calc
      }
    };
    calculate();
    window.addEventListener("resize", calculate);
    return () => window.removeEventListener("resize", calculate);
  }, []);

  return cardWidth;
};

// Gemstone Modal Component
const GemstoneModal = ({ stone, isOpen, onClose }) => {
  if (!isOpen || !stone) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl"
          style={{
            background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
            border: "1px solid rgba(212, 175, 55, 0.3)",
            boxShadow: `0 0 60px ${stone.glowColor}`,
          }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`gemstone-modal-${stone.id}`}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
            aria-label="Close modal"
          >
            <X size={20} className="text-white" />
          </button>

          {/* Header with Image */}
          <div
            className={`relative h-48 bg-gradient-to-br ${stone.color} overflow-hidden`}
          >
            <img
              src={stone.image}
              alt={stone.name}
              className="w-full h-full object-cover mix-blend-overlay opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent" />
            <div className="absolute bottom-4 left-6">
              <h3 className="text-2xl sm:text-3xl font-bold text-white">
                {stone.name}
              </h3>
              <p className="text-[#D4AF37] font-medium">
                {stone.hindiName} • {stone.planet}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
            {/* Zodiac Signs */}
            <div>
              <h4 className="text-[#D4AF37] text-sm uppercase tracking-wider mb-2">
                Associated Zodiac Signs
              </h4>
              <div className="flex gap-2 flex-wrap">
                {stone.zodiacSigns.map((sign) => (
                  <span
                    key={sign}
                    className="px-4 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-sm"
                  >
                    {sign}
                  </span>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h4 className="text-[#D4AF37] text-sm uppercase tracking-wider mb-3">
                Key Benefits
              </h4>
              <ul className="space-y-2">
                {stone.benefits.map((benefit, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-[#94A3B8]"
                  >
                    <Sparkles
                      size={16}
                      className="text-[#D4AF37] mt-1 flex-shrink-0"
                    />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Who Should Wear */}
            <div>
              <h4 className="text-[#D4AF37] text-sm uppercase tracking-wider mb-2">
                Who Should Wear
              </h4>
              <p className="text-[#94A3B8] leading-relaxed">
                {stone.whoShouldWear}
              </p>
            </div>

            {/* Outcomes */}
            <div>
              <h4 className="text-[#D4AF37] text-sm uppercase tracking-wider mb-3">
                Expected Outcomes
              </h4>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {stone.outcomes.map((outcome, index) => {
                  const Icon = outcome.icon;
                  return (
                    <div
                      key={index}
                      className="flex flex-col items-center p-2 sm:p-3 rounded-xl bg-white/5 border border-white/10"
                    >
                      <Icon size={22} className="text-[#D4AF37] mb-1 sm:mb-2" />
                      <span className="text-[#F8FAFC] text-xs sm:text-sm text-center">
                        {outcome.text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Caution */}
            <div className="p-3 sm:p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
              <h4 className="text-amber-400 text-sm font-medium mb-1">
                Important Note
              </h4>
              <p className="text-[#94A3B8] text-sm">{stone.caution}</p>
            </div>

            {/* CTA Buttons */}
            <div className="pt-2 sm:pt-4 space-y-3">
              <p className="text-center text-[#94A3B8] text-sm">
                Get personalized gemstone recommendation based on your birth
                chart
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`${WHATSAPP_BASE}${encodeURIComponent(`Hello Guru Maa, I am interested in ${stone.name} (${stone.hindiName}). Please guide me if it's suitable for my horoscope.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-full bg-[#25D366] hover:bg-[#128C7E] transition-colors font-semibold text-white"
                  data-testid={`modal-whatsapp-${stone.id}`}
                >
                  <MessageCircle size={20} />
                  <span>Consult on WhatsApp</span>
                </a>
                <a
                  href={`tel:${PHONE_NUMBER}`}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B4941F] text-[#0F172A] font-semibold hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all"
                  data-testid={`modal-call-${stone.id}`}
                >
                  <Phone size={20} />
                  <span>Call Now</span>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Gemstone Card Component
const GemstoneCard = ({ stone, onClick, index, isInView }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: Math.min(index * 0.1, 0.5) }}
      onClick={() => onClick(stone)}
      className="group relative cursor-pointer h-full"
      data-testid={`gemstone-card-${stone.id}`}
    >
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm transition-all duration-500 hover:border-[#D4AF37]/50 hover:shadow-xl h-full flex flex-col">
        {/* Hover Glow Effect */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at center, ${stone.glowColor} 0%, transparent 70%)`,
          }}
        />

        {/* Image */}
        <div
          className={`relative h-48 sm:h-52 bg-gradient-to-br ${stone.color} overflow-hidden flex-shrink-0`}
        >
          <img
            src={stone.image}
            alt={stone.name}
            className="w-full h-full object-cover mix-blend-overlay opacity-50 group-hover:opacity-70 group-hover:scale-110 transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent" />

          {/* Planet Badge */}
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/20">
            <span className="text-[#D4AF37] text-xs font-medium">
              {stone.planet}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 flex flex-col flex-1">
          <h3 className="text-lg sm:text-xl font-bold text-[#F8FAFC] mb-1 group-hover:text-[#D4AF37] transition-colors">
            {stone.name}
          </h3>
          <p className="text-[#94A3B8] text-sm mb-3">{stone.hindiName}</p>

          {/* Zodiac Tags */}
          <div className="flex gap-2 flex-wrap mb-4">
            {stone.zodiacSigns.map((sign) => (
              <span
                key={sign}
                className="px-2 py-0.5 rounded-full bg-white/10 text-[#94A3B8] text-xs"
              >
                {sign}
              </span>
            ))}
          </div>

          {/* View Details CTA */}
          <div className="flex items-center justify-between mt-auto">
            <span className="text-[#D4AF37] text-sm font-medium group-hover:underline">
              View Details
            </span>
            <motion.div
              className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"
              whileHover={{ scale: 1.1 }}
            >
              <Sparkles size={16} className="text-[#D4AF37]" />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Gemstones = () => {
  const ref = useRef(null);
  const scrollRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedStone, setSelectedStone] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const cardWidth = useCardWidth();

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);

      // Update active dot index (mobile)
      const isMobile = window.innerWidth < 640;
      if (isMobile && cardWidth) {
        const idx = Math.round(scrollLeft / cardWidth);
        setActiveIndex(Math.min(idx, gemstones.length - 1));
      }
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      // On mobile/tablet use exact card width; on desktop scroll by container/5
      let scrollAmount;
      if (cardWidth) {
        scrollAmount = direction === "left" ? -cardWidth : cardWidth;
      } else {
        scrollAmount =
          direction === "left"
            ? -scrollRef.current.clientWidth / 5
            : scrollRef.current.clientWidth / 5;
      }
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      setTimeout(checkScroll, 300);
    }
  };

  // Scroll to specific index (mobile dots)
  const scrollToIndex = (index) => {
    if (scrollRef.current && cardWidth) {
      scrollRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
    }
  };

  // Card width style
  const getCardStyle = () => {
    if (cardWidth) {
      return { width: `${cardWidth}px`, flexShrink: 0 };
    }
    // Desktop: 5 cards with 4 gaps of 16px
    return { width: "calc((100% - 64px) / 5)", flexShrink: 0 };
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

  return (
    <section
      id="gemstones"
      className="relative py-20 md:py-32"
      data-testid="gemstones-section"
      ref={ref}
    >
      {/* Section Divider */}
      <div className="section-divider mb-20" />

      {/* Background Effects */}
      <div className="cosmic-glow absolute top-1/4 left-0 opacity-15" />
      <div className="cosmic-glow absolute bottom-1/4 right-0 opacity-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <span className="text-[#D4AF37] text-sm uppercase tracking-widest font-medium">
            Celestial Gemstones
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4">
            Sacred <span className="text-gold-gradient">Stone Guidance</span>
          </h2>
          <p className="mt-4">
            Harness the cosmic power of precious gemstones for prosperity,
            protection, and spiritual growth
          </p>
        </motion.div>

        {/* Gemstones Carousel */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="relative"
        >
          {/* Navigation Buttons — always visible, justified between on mobile, end on desktop */}
          <div className="flex justify-between sm:justify-end gap-2 mb-6">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              className={`p-3 rounded-full border border-white/10 transition-all ${
                canScrollLeft
                  ? "hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#F8FAFC]"
                  : "opacity-30 cursor-not-allowed text-[#94A3B8]"
              }`}
              aria-label="Scroll left"
              data-testid="gemstones-scroll-left"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              className={`p-3 rounded-full border border-white/10 transition-all ${
                canScrollRight
                  ? "hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 text-[#F8FAFC]"
                  : "opacity-30 cursor-not-allowed text-[#94A3B8]"
              }`}
              aria-label="Scroll right"
              data-testid="gemstones-scroll-right"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-4 overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-4"
            style={{ scrollSnapType: "x mandatory" }}
          >
            {gemstones.map((stone, index) => (
              <div
                key={stone.id}
                className="snap-start"
                style={{
                  ...getCardStyle(),
                  scrollSnapAlign: "start",
                }}
              >
                <GemstoneCard
                  stone={stone}
                  index={index}
                  isInView={isInView}
                  onClick={setSelectedStone}
                />
              </div>
            ))}
          </div>

          {/* Mobile Dot Indicators */}
          <div className="flex sm:hidden justify-center gap-2 mt-5">
            {gemstones.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollToIndex(i)}
                className={`transition-all duration-300 rounded-full ${
                  activeIndex === i
                    ? "w-6 h-2 bg-[#D4AF37]"
                    : "w-2 h-2 bg-white/20"
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Mobile swipe hint — only on first render */}
          <p className="sm:hidden text-center text-[#94A3B8]/50 text-xs mt-3">
            Swipe to explore all gemstones
          </p>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-[#94A3B8] mb-6">
            Not sure which gemstone is right for you?{" "}
            <span className="text-[#D4AF37]">Get personalized guidance.</span>
          </p>
          <a
            href={`${WHATSAPP_BASE}${encodeURIComponent(
              "Hello Guru Maa, I would like a personalized gemstone recommendation based on my birth chart. Please guide me.",
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 whatsapp-btn px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold"
            data-testid="gemstones-main-cta"
          >
            <MessageCircle size={22} />
            <span>Get Your Gemstone Recommendation</span>
          </a>
        </motion.div>
      </div>

      {/* Gemstone Detail Modal */}
      <GemstoneModal
        stone={selectedStone}
        isOpen={!!selectedStone}
        onClose={() => setSelectedStone(null)}
      />
    </section>
  );
};

export default Gemstones;
